// Utility function to normalize column names for matching
export const normalizeColumnName = (str: string): string => {
  // Convert to lowercase and remove all separators (_, -, spaces)
  return str.toLowerCase().replace(/[_\s-]/g, "")
}

// Convert camelCase to normalized form
export const camelToNormalized = (str: string): string => {
  // Insert separator before uppercase letters, then normalize
  return str
    .replace(/([a-z])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .replace(/[_\s-]/g, "")
}

// Calculate similarity between two strings using Levenshtein distance
export const calculateSimilarity = (str1: string, str2: string): number => {
  const longer = str1.length > str2.length ? str1 : str2
  const shorter = str1.length > str2.length ? str2 : str1
  if (longer.length === 0) return 1.0

  const editDistance = (s1: string, s2: string): number => {
    const costs: number[] = []
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j
        } else if (j > 0) {
          let newValue = costs[j - 1]
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1
          }
          costs[j - 1] = lastValue
          lastValue = newValue
        }
      }
      if (i > 0) costs[s2.length] = lastValue
    }
    return costs[s2.length]
  }

  return (longer.length - editDistance(shorter, longer)) / longer.length
}

// Find best column match using multiple strategies
export const findBestColumnMatch = (
  templateColumnName: string,
  availableColumns: string[],
): string | undefined => {
  const normalizedTemplate = normalizeColumnName(templateColumnName)
  const camelNormalizedTemplate = camelToNormalized(templateColumnName)

  // Strategy 1: Exact match after normalization
  const exactMatch = availableColumns.find((col) => {
    const normalizedCol = normalizeColumnName(col)
    const camelNormalizedCol = camelToNormalized(col)
    return (
      normalizedCol === normalizedTemplate ||
      normalizedCol === camelNormalizedTemplate ||
      camelNormalizedCol === normalizedTemplate ||
      camelNormalizedCol === camelNormalizedTemplate
    )
  })
  if (exactMatch) return exactMatch

  // Strategy 2: Check if one contains the other (for partial matches)
  const containsMatch = availableColumns.find((col) => {
    const normalizedCol = normalizeColumnName(col)
    return (
      normalizedCol.includes(normalizedTemplate) ||
      normalizedTemplate.includes(normalizedCol)
    )
  })
  if (containsMatch) return containsMatch

  // Strategy 3: Levenshtein distance for fuzzy matching
  let bestMatch: string | undefined
  let bestScore = 0.7 // Minimum threshold for fuzzy matching

  availableColumns.forEach((col) => {
    const normalizedCol = normalizeColumnName(col)
    const score = calculateSimilarity(normalizedTemplate, normalizedCol)
    if (score > bestScore) {
      bestScore = score
      bestMatch = col
    }
  })

  return bestMatch
}
